import { useFocusEffect, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ForceUpdateResult } from 'services/forceUpdate/forceUpdateManager';
import { mapJsonToAppUpdates } from 'services/forceUpdate/helpers';
import { AppUpdates } from 'services/forceUpdate/types';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import { isAndroid, isWeb } from 'shared/constants/platform';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';
import { downloadVaroApp } from 'shared/utils/downloadVaroApp';

interface SystemForceUpdateProps {
  forceUpdateResult: ForceUpdateResult;
}

const SystemForceUpdate = ({ forceUpdateResult }: SystemForceUpdateProps) => {
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);

  const { t: translatedText } = useTranslation('forceUpdate');
  const { t: buttonText } = useTranslation('forceUpdate', {
    keyPrefix: 'buttons',
  });
  const { top } = useSafeAreaInsets();
  const [primaryButtonVisible, setPrimaryButtonVisible] = useState(false);

  // Apply translations to raw config now that we're in React context
  const translatedConfig = useMemo(() => {
    const rawConfig = forceUpdateResult.rawConfig ?? ({} as AppUpdates);
    return mapJsonToAppUpdates(rawConfig, translatedText);
  }, [forceUpdateResult.rawConfig, translatedText]);

  const isServiceDown = forceUpdateResult.isServiceDown;
  const dueToMinSupportedVersion = forceUpdateResult.dueToMinSupportedVersion;
  const dueToTargetSupportedVersion =
    forceUpdateResult.dueToTargetSupportedVersion;

  // Get service maintenance details from translated config
  const serviceDownDetails = translatedConfig.serviceMaintenance;

  // Get supported version details from translated config (platform-specific)
  const supportedVersionDetails = {
    title: translatedConfig.supportedVersions?.title ?? '',
    message: translatedConfig.supportedVersions?.message ?? '',
    ...(isAndroid
      ? translatedConfig.supportedVersions?.android
      : translatedConfig.supportedVersions?.ios),
  };

  const handleUpdateButtons = useCallback(() => {
    const updateRequired = Boolean(
      dueToMinSupportedVersion || dueToTargetSupportedVersion,
    );
    setPrimaryButtonVisible(updateRequired);
  }, [dueToMinSupportedVersion, dueToTargetSupportedVersion]);

  // Determine title and message based on state
  let title = '';
  let message = '';

  if (serviceDownDetails?.showMessageInApp) {
    title = serviceDownDetails.title;
    message = serviceDownDetails.message;
  } else if (!isWeb) {
    title = supportedVersionDetails.title;
    message = supportedVersionDetails.message;
  }
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted && isWeb && !isServiceDown) {
      router.replace('/login');
    }
    if (!serviceDownDetails?.showMessageInApp) {
      // Update/Upgrade needed
      handleUpdateButtons();
    }
  }, [
    handleUpdateButtons,
    isMounted,
    isServiceDown,
    serviceDownDetails?.showMessageInApp,
    router,
  ]);

  const okButtonPress = async () => {
    if (!isWeb) {
      await downloadVaroApp();
    } else {
      router.replace('/login');
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (isServiceDown && isWeb) {
        const handleBackButton = (event: Event) => {
          window.history.pushState(null, '', '/force-update');
          event.preventDefault();
        };
        window.addEventListener('popstate', handleBackButton);
        window.history.pushState(null, '');
        return () => {
          window.removeEventListener('popstate', handleBackButton);
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  const handleBackPress = useCallback(() => {
    return false;
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (isServiceDown) {
        const backHandler = BackHandler.addEventListener(
          'hardwareBackPress',
          handleBackPress,
        );
        return () => {
          backHandler.remove();
        };
      }
    }, [handleBackPress, isServiceDown]),
  );

  return (
    <View style={[styles.container, { paddingTop: top }]}>
      {title && message ? (
        <ActionScreen
          visual="COFFEE_A_ANIMATED"
          titleText={title}
          titleTextStyle="extra-large"
          bodyContent={
            <HTMLRenderer
              htmlAsString={message}
              tagsStyles={{
                body: {
                  fontFamily:
                    VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
                  fontStyle: 'normal',
                  lineHeight: 22,
                  letterSpacing: 0.32,
                },
              }}
            />
          }
          {...(primaryButtonVisible
            ? {
                primaryButton: {
                  label: buttonText('updateNow'),
                  onPress: okButtonPress,
                },
              }
            : {})}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default SystemForceUpdate;
